import { Button, Icon, Tab } from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';
import { FC, type ReactNode } from 'react';

import { tableStore } from '../stores/table-store';
import { useSolutionHealth } from './use-solution-health';
import { isGroupRow } from '../stores/utils/group-row-guards';
import { buildCanvasAgentNodes } from './canvas-view/canvas-view-model';
import { parseViewMode, type WorkflowsViewMode } from './workflows-view-mode';

import './build-topbar.scss';
import { BobcatTabGroup } from '../components/bobcat-tab-group';

interface BuildTopbarProps {
  value: WorkflowsViewMode;
  onChange: (next: WorkflowsViewMode) => void;
  showCanvasView?: boolean;
  /** Controls that only apply to the current view — the canvas's find, error
   *  routing and zoom. Rendered on the right. */
  children?: ReactNode;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * The Build stage's one bar, with `bobcat-five-stage-nav` on — the prototype's
 * `.topbar` (`prostudio-build-flow-layouts.html`): what the solution IS (the
 * title, the switcher between its readings, its size) on the left, what you
 * can DO to it on the right. It replaces the floating Table View / Test View /
 * Canvas segmented control, which sat over the canvas and covered its top
 * card. In normal flow, so nothing it sits above is hidden under it.
 *
 * No Test tab: testing lives in the Validate stage. Files is the old
 * standalone Media and Files page. Actions — the old standalone Actions page
 * — is a button on the right that opens its panel over the view (Bobcat
 * status check, 2026-09-29): build material, not another reading of the
 * solution.
 */
export const BuildTopbar: FC<BuildTopbarProps> = observer(
  ({ value, onChange, showCanvasView, children }) => {
    const rows = tableStore.getAllSortedRows();
    const agentCount = buildCanvasAgentNodes(rows).length;
    const nodeCount = rows.filter((row) => !isGroupRow(row)).length;
    const health = useSolutionHealth();
    const healthErrors = health.filter((i) => i.severity === 'error').length;
    const healthWarnings = health.filter(
      (i) => i.severity === 'warning'
    ).length;

    return (
      <div className="c-build-topbar" data-testid="bobcat-build-topbar">
        <h1 className="c-build-topbar__title">Build</h1>
        <BobcatTabGroup
          variant="flush"
          test-id="bobcat-build-view-tabs"
          onTabShow={(e: CustomEvent<{ name: string }>) =>
            onChange(parseViewMode(e.detail.name))
          }
        >
          {showCanvasView && (
            <Tab slot="tab" panel="canvas" active={value === 'canvas'}>
              <span className="c-build-topbar__tab">
                <Icon name="node" size="xsmall" />
                Canvas
              </span>
            </Tab>
          )}
          <Tab slot="tab" panel="table" active={value === 'table'}>
            <span className="c-build-topbar__tab">
              <Icon name="grid-menu" size="xsmall" />
              Table
            </span>
          </Tab>
          <Tab slot="tab" panel="files" active={value === 'files'}>
            <span className="c-build-topbar__tab">
              <Icon name="content-image" size="xsmall" />
              Files
            </span>
          </Tab>
        </BobcatTabGroup>
        <span className="c-build-topbar__size">
          {plural(agentCount, 'agent')} · {plural(nodeCount, 'node')}
        </span>
        <span className="c-build-topbar__spacer" />
        {children}
        <Button
          size="small"
          variant={tableStore.healthPanelOpen ? 'secondary' : 'ghost'}
          test-id="bobcat-build-health-toggle"
          aria-label={`Health: ${healthErrors} to fix, ${healthWarnings} to look at`}
          aria-pressed={tableStore.healthPanelOpen}
          onClick={() =>
            tableStore.setHealthPanelOpen(!tableStore.healthPanelOpen)
          }
        >
          <Icon slot="prefix" name="check" size="xsmall" />
          <span className="c-build-topbar__actions-label">Health</span>
          {(healthErrors > 0 || healthWarnings > 0) && (
            <span
              className={`c-build-topbar__badge c-build-topbar__badge--${
                healthErrors ? 'error' : 'warning'
              }`}
            >
              {healthErrors || healthWarnings}
            </span>
          )}
        </Button>
        <Button
          size="small"
          variant={tableStore.actionsPanelOpen ? 'secondary' : 'ghost'}
          test-id="bobcat-build-actions-toggle"
          aria-label="Actions"
          aria-pressed={tableStore.actionsPanelOpen}
          onClick={() =>
            tableStore.setActionsPanelOpen(!tableStore.actionsPanelOpen)
          }
        >
          {/* The commands' own grey code mark, not the action-node diamond
              — that's the node that runs them (prototype `#cvActions`). */}
          <Icon
            slot="prefix"
            name="layout-code"
            size="small"
            className="c-build-topbar__actions-icon"
          />
          <span className="c-build-topbar__actions-label">Actions</span>
        </Button>
      </div>
    );
  }
);
